import { component } from 'picoapp';
import fetchProductBySku from '~/js/utils/fetch-product-by-sku';

export default component(async (node) => {
  const { currentSku } = node.dataset;
  const skuList = JSON.parse(
    node.querySelector('[data-sku-list]').innerHTML,
  );
  const selector = node.querySelector('[data-linked-product-selector]');
  const loadingEl = node.querySelector('[data-linked-loading]');
  const errorEl = node.querySelector('[data-linked-error]');

  const maxRetries = 3;

  const MATERIAL_PREFIX = 'filter:Material:';
  const MATERIALE_PREFIX = 'filter:Materiale:';

  /**
   * Extract material from product tags
   */
  const extractMaterial = (tags) => {
    const materialTag = tags.find(
      (tag) => tag.startsWith(MATERIAL_PREFIX) || tag.startsWith(MATERIALE_PREFIX),
    );

    return materialTag
      ? materialTag.replace(MATERIAL_PREFIX, '').replace(MATERIALE_PREFIX, '')
      : '';
  };

  /**
   * Show loading state
   */
  const showLoading = (show = true) => {
    if (loadingEl) {
      loadingEl.classList.toggle('hidden', !show);
    }
  };

  /**
   * Show error state
   */
  const showError = (show = true) => {
    if (errorEl) {
      errorEl.classList.toggle('hidden', !show);
    }
  };

  /**
   * Fetch product data with retry logic
   */
  // eslint-disable-next-line consistent-return
  const fetchProduct = async (sku) => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await fetchProductBySku(sku);
      } catch (error) {
        console.warn(`Attempt ${attempt + 1} failed for SKU ${sku}:`, error);

        if (attempt === maxRetries) {
          throw error;
        }

        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, 1000 * (2 ** attempt));
        });
      }
    }
  };

  /**
   * Load linked products and populate selector
   */
  const loadLinkedProducts = async () => {
    showLoading(true);
    showError(false);

    try {
      // Filter out current product SKU and fetch others
      const otherSkus = skuList.filter((sku) => sku !== currentSku);

      if (otherSkus.length === 0) {
        // No linked products, hide the component
        node.style.display = 'none';
        return;
      }

      // Fetch all products in parallel

      const fetchPromises = otherSkus.map((sku) => fetchProduct(sku));
      const products = await Promise.all(fetchPromises);

      // Filter out null results
      const validProducts = products.filter((product) => product !== null);

      if (validProducts.length === 0) {
        // No valid linked products found
        node.style.display = 'none';
        return;
      }

      // Clear placeholder options
      const placeholders = selector.querySelectorAll('[data-placeholder]');

      placeholders.forEach((placeholder) => placeholder.remove());

      // Add linked products to selector

      validProducts.forEach((product) => {
        const material = extractMaterial(product.tags);
        const option = document.createElement('option');

        option.value = product.handle;
        option.dataset.sku = product.variants[0]?.sku || '';
        option.dataset.material = material;
        option.textContent = material || product.title;

        selector.appendChild(option);
      });

      showLoading(false);
    } catch (error) {
      console.error('❌ STL Linked: Failed to load linked products:', error);
      showLoading(false);
      showError(true);
    }
  };

  /**
   * Handle linked product selection
   */
  const handleSelection = () => {
    const selectedOption = selector.options[selector.selectedIndex];

    if (!selectedOption) return;

    const productHandle = selectedOption.value;

    // Only trigger product change if it's different from current
    if (productHandle !== node.closest('[data-form-container]')?.dataset.currentHandle) {
      const event = new CustomEvent('stl:product:change', {
        detail: {
          handle: productHandle,
          source: 'linked-products',
        },
      });

      document.dispatchEvent(event);
    }
  };

  /**
   * Initialize component
   */
  const init = async () => {
    // Add event listener for selection changes
    selector.addEventListener('change', handleSelection);

    // Load linked products
    await loadLinkedProducts();
  };

  // Initialize
  await init();
});
